'use client';

import { useCallback } from 'react';
import {
	encodeName,
	getReferrerNamePublicKeySync,
	getRevenueShareAccountPublicKey,
	getUserAccountPublicKeySync,
	getUserStatsAccountPublicKey,
} from '@velocity-exchange/sdk';
import { createRevenueShareAccountIx } from '@velocity-exchange/common';
import {
	SYSVAR_RENT_PUBKEY,
	SystemProgram,
	TransactionInstruction,
} from '@solana/web3.js';
import invariant from 'tiny-invariant';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { notify } from 'src/utils/notifications';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';
import useVelocityClient from 'src/hooks/useVelocityClient';
import { UiEnhancedIWalletV2 } from 'src/utils/wallets/IWalletV2';
import { useSponsoredSignVelocityActionWithFallback } from 'src/hooks/wallets/useSponsoredSignVelocityActionWithFallback';
import { useGetTxParams } from 'src/hooks/velocityActions/transactions/useGetTxParams';

/**
 * Hook for creating referral links.
 */
export const useCreateReferralLink = () => {
	const velocityClient = useVelocityClient();
	const isGeoblocked = useVelocityStore((s) => s.isGeoblocked);
	const { captureEvent } = usePostHogCapture();
	const getTxParams = useGetTxParams();

	// TODO: abstract into @velocity-exchange/common
	const createReferralLinkAction = useCallback(
		async (wallet: UiEnhancedIWalletV2, referrerName: string) => {
			invariant(velocityClient, 'driftClient is required');

			// Referrer names can only be created from subaccount 0, regardless of
			// which subaccount is currently active in the UI.
			const authority = velocityClient.wallet.publicKey;
			const subAccountZeroPublicKey = getUserAccountPublicKeySync(
				velocityClient.program.programId,
				authority,
				0
			);

			const userStatsAccountPublicKey = getUserStatsAccountPublicKey(
				velocityClient.program.programId,
				authority
			);

			const nameBuffer = encodeName(referrerName);
			const referrerNameAccountPublicKey = getReferrerNamePublicKeySync(
				velocityClient.program.programId,
				nameBuffer
			);
			const ix =
				await velocityClient.program.instruction.initializeReferrerName(
					nameBuffer,
					{
						accounts: {
							referrerName: referrerNameAccountPublicKey,
							user: subAccountZeroPublicKey,
							authority,
							userStats: userStatsAccountPublicKey,
							payer: velocityClient.wallet.publicKey,
							rent: SYSVAR_RENT_PUBKEY,
							systemProgram: SystemProgram.programId,
						},
					}
				);

			const ixs: TransactionInstruction[] = [ix];

			// In the RevenueShare referral model the referrer accrues referee fees
			// into a RevenueShare account (PDA ["REV_SHARE", authority]); without it
			// there is nowhere on-chain for referral rewards to settle. Create it in
			// the same transaction so a referral link is never left without an
			// accrual account. Guard on existence: a user can hold multiple referrer
			// names, and a duplicate initializeRevenueShare would revert the whole tx.
			const revenueShareAccountPublicKey = getRevenueShareAccountPublicKey(
				velocityClient.program.programId,
				authority
			);
			const revenueShareAccountInfo =
				await velocityClient.connection.getAccountInfo(
					revenueShareAccountPublicKey
				);
			if (!revenueShareAccountInfo) {
				ixs.push(
					await createRevenueShareAccountIx({
						velocityClient,
						authority,
						payer: velocityClient.wallet.publicKey,
					})
				);
			}

			await wallet.signAndSendTransaction(ixs, getTxParams());
		},
		[velocityClient, getTxParams]
	);

	const wrappedCreateReferralLink = useSponsoredSignVelocityActionWithFallback(
		createReferralLinkAction
	);

	const createReferralLink = useCallback(
		async (referrerName: string): Promise<boolean> => {
			if (isGeoblocked) {
				notify({
					type: 'error',
					message: 'Geoblocked',
					description:
						'You are not allowed to use Velocity from a restricted territory',
					lengthMs: 5000,
				});
				return false;
			}

			if (!velocityClient) return false;

			const toastId = `create-referral-link-${referrerName}`;

			notify({
				type: 'awaiting_wallet_confirmation',
				id: toastId,
				showUntilCancelled: true,
			});

			try {
				await wrappedCreateReferralLink(referrerName);

				notify({
					type: 'success',
					message: 'Referral link created.',
					id: toastId,
					updatePrevious: true,
					showUntilCancelled: false,
				});

				return true;
			} catch (e: any) {
				captureEvent('onboarding_referral_creation_error', {
					error: e,
				});
				console.error(e);
				notify({
					type: 'error',
					message: 'Could not create referral link.',
					id: toastId,
					updatePrevious: true,
					showUntilCancelled: false,
				});
				return false;
			}
		},
		[velocityClient, isGeoblocked, captureEvent, wrappedCreateReferralLink]
	);

	return { createReferralLink };
};
